import React from 'react';
import { KbSection, Callout, StepList, VisibleTo } from '../../components/knowledge/primitives.jsx';
import { ColumnsGlyph, StarGlyph, ChevronGlyph } from '../../components/knowledge/InlineIcons.jsx';
import CaseListOverviewDemo from '../../components/knowledge/demos/CaseListOverviewDemo.jsx';
import SearchFilterDemo from '../../components/knowledge/demos/SearchFilterDemo.jsx';
import StatusFlowDemo from '../../components/knowledge/demos/StatusFlowDemo.jsx';
import LegalBarDemo from '../../components/knowledge/demos/LegalBarDemo.jsx';

// "Finding & viewing cases" articles. All seven are visible to every role —
// tier-specific fragments (the Cost column, the Costs tab, the Report tab)
// are gated per section with VisibleTo, mirroring the app's own gates.

// Inline badge samples for the badges article — real classes, placeholder text.
function BadgeRow({ children }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, margin: '10px 0 4px' }}>
      {children}
    </div>
  );
}

function CaseListBody({ user }) {
  return (
    <>
      <KbSection title="One list, every case you can see">
        <p>
          <strong>All Cases</strong> in the sidebar is the master list. It is already scoped
          to your access — you only ever see the cases your role covers, so there is nothing
          to switch or request. The <strong>quick filters</strong> along the top (<strong>New</strong>,{' '}
          <strong>Open</strong>, <strong>Pending</strong>, <strong>With Claims</strong>,{' '}
          <strong>Closed</strong>) double as shortcuts: click one to focus the list on exactly
          that status. The <strong>Quick filters</strong> button in the page header hides them
          when you want more room for the table.
        </p>
        <CaseListOverviewDemo />
      </KbSection>

      <KbSection title="The filter sidebar" delay={1}>
        <p>The panel on the left stacks the filter groups; each shows a live count next to every option:</p>
        <StepList steps={[
          <><strong>Status</strong> — New, Open, Pending, With Claims, Closed. Closed starts unticked, so the default view is active work.</>,
          <><strong>Event</strong> — LHR, AWI, Near miss, Clinic incident, Other treatments.</>,
          <><strong>Severity</strong> — the severity codes plus <strong>No code</strong> for cases without one.</>,
          <><strong>Country</strong>, <strong>Clinic</strong> and <strong>Tags</strong> — press the <strong>+</strong> beside the title to pick from a dialog; your picks come back as pills you can remove one by one. Tags can be included <em>or excluded</em> (see the Tags article).</>,
          <><strong>Inactive for</strong> — only cases with no comment, upload, edit or status change for 7, 14, 30 or 90+ days.</>,
          <><strong>Date range</strong> — from Last 7 days up to All time, or a custom From/To range.</>,
        ]} />
        <p>
          <strong>Reset</strong> at the top of the panel returns everything to the defaults.
          Groups fold to fit the height of the table; a folded group shows how many of its
          options are active. Above the table, <strong>Selected</strong> is what your filters
          pick, <strong>All</strong> shows every case with the filters set aside, and{' '}
          <strong>Deleted</strong> is the bin. On smaller screens the sidebar collapses behind
          a <strong>Filters</strong> button.
        </p>
        <Callout tone="info">
          Which groups appear — and the status and severity names inside them — comes from your
          admin's configuration, so your list may differ slightly from these examples.
        </Callout>
      </KbSection>

      <KbSection title="Sorting and paging" delay={2}>
        <p>
          Click a column heading to sort by it; click again to flip the direction. The default
          order is <strong>Reported</strong>, newest first. The list shows 20 cases per page by
          default (your admin can change this) — the pager underneath the table shows where you
          are, e.g. <em>Showing 1–20 of 132</em>.
        </p>
      </KbSection>

      <KbSection title="The columns, and opening a case" delay={3}>
        <p>
          Each row shows <strong>Case ID</strong>, <strong>Clinic</strong>, <strong>Event</strong>{' '}
          (the treatment, or a Near miss / Clinic incident tag), <strong>Severity</strong>,{' '}
          <strong>Status</strong>, <strong>Staff Member</strong> and <strong>Reported</strong>.
          <VisibleTo user={user} when={(u) => !!u?.canSeeCosts}>
            {' '}A <strong>Cost</strong> column on the right totals the case's cost lines per
            currency — it sorts like any other column.
          </VisibleTo>
        </p>
        <p>
          Click anywhere on a row — or focus it and press <strong>Enter</strong> — to open the
          case. Once inside, the <strong>Previous</strong> and <strong>Next</strong> buttons walk
          the same filtered list in order, so you can work through results without going back.
        </p>
      </KbSection>

      <KbSection title="Choosing your columns" delay={4}>
        <p>
          The <strong>columns button</strong> <ColumnsGlyph /> at the right of the toolbar
          opens a menu of every optional column. Tick one on or off to change what the table
          shows. <strong>Case ID</strong> is always there; everything else is yours to arrange.{' '}
          <strong>SLA</strong> (the response clock) is off until you switch it on, so turn it on
          if you chase deadlines. Hiding the column you're sorting by drops back to the default sort.
        </p>
        <p>
          Your choice is remembered <strong>on this device</strong> — it isn't shared with anyone.
          <VisibleTo user={user} when={(u) => u?.role !== 'ClinicManager' && u?.role !== 'ClinicMailbox'}>
            {' '}It also travels into a <em>saved view</em> if you keep one (below), so a view can
            carry its own set of columns.
          </VisibleTo>
        </p>
      </KbSection>

      <KbSection title="Finding stale cases" delay={5}>
        <p>
          Two columns exist for chasing work that has gone quiet. <strong>Last activity</strong>{' '}
          is how long since anything human happened on the case — a comment, upload, edit or
          status change; notification emails don't count — and turns amber past 30 days.{' '}
          <strong>SLA</strong> shows the response clock: whose turn it is and the deadline, red
          when it is overdue. Sort by SLA to bring the most urgent cases to the top, and pair
          it with the <strong>Inactive for</strong> filter to list what nobody has touched.
        </p>
      </KbSection>

      <VisibleTo user={user} when={(u) => u?.role !== 'ClinicManager' && u?.role !== 'ClinicMailbox'}>
        <KbSection title="Saving your views" delay={6}>
          <p>
            A <strong>view</strong> is a way you like to look at All Cases — a set of filters, the
            columns you have on, and the sort order, kept together under a name. Set the list up
            the way you want it, and a <strong>star</strong> <StarGlyph /> appears next to the
            “All Cases” title: that means you have unsaved changes worth keeping.
          </p>
          <StepList steps={[
            <>Filter, choose columns and sort the list however suits the work in front of you.</>,
            <>Tap the <strong>star</strong> <StarGlyph /> by the title and give the view a name — for example <em>My open Code 4s</em>.</>,
            <>Tick <strong>Make this my default view</strong> if you want it applied automatically whenever you open All Cases.</>,
            <>Press <strong>Save view</strong>.</>,
          ]} />
          <p>
            Once you have saved at least one, a <strong>chevron</strong> <ChevronGlyph /> sits next
            to the title. Open it to switch between your views — the one in use is ticked, and the title changes
            to its name so you always know what you are looking at. The list starts with{' '}
            <strong>All Cases</strong>, which takes you back to the unfiltered default at any time.
          </p>
          <p>
            <strong>Edit views…</strong> at the bottom of that menu lets you rename a view, change
            which one is the default, or delete one. Deleting the view you are on simply returns you
            to All Cases.
          </p>
          <Callout tone="info">
            Views are <strong>private to you</strong> — nobody else sees or shares them, and they
            follow you to any device you sign in on. They are a personal shortcut, not a change to
            anyone else’s list.
          </Callout>
        </KbSection>
      </VisibleTo>
    </>
  );
}

function SearchBody() {
  return (
    <>
      <KbSection title="What the search matches">
        <p>
          The search box above the table (<em>"Search by case ID, clinic, therapist…"</em>) matches five
          things: the <strong>case reference</strong>, the <strong>clinic</strong>, the{' '}
          <strong>region</strong>, the <strong>case title</strong> — the client name on a
          treatment case, or the incident title on a near miss / clinic incident — and the{' '}
          <strong>staff member concerned</strong> (therapist name). If you type
          several words, a case must match <em>every</em> word to stay in the list.
        </p>
        <SearchFilterDemo />
      </KbSection>

      <KbSection title="Finding a case fast" delay={1}>
        <StepList steps={[
          <>Know the reference? Type just the number — <strong>42</strong> is treated as a case-ID lookup and finds AE-42 straight away.</>,
          <>Exact reference matches always rank first, so the case you named sits at the top even when other rows also match.</>,
          <>Combine words to narrow: a clinic name plus a number keeps only that clinic's cases whose reference contains the number.</>,
        ]} />
      </KbSection>

      <KbSection title="When nothing seems to match" delay={2}>
        <p>
          Search works <em>within</em> your active filters. If your search finds cases that the
          filters exclude — a closed case while Closed is unticked, say — the list shows a{' '}
          <em>"you might be looking for"</em> strip with those cases instead of a bare zero, plus
          a <strong>Reset filters</strong> button to clear the way.
        </p>
        <Callout tone="info">
          Still nothing? The case may be outside your access scope — search only covers cases
          your role can see.
        </Callout>
      </KbSection>
    </>
  );
}

function CaseDetailTourBody({ user }) {
  return (
    <>
      <KbSection title="The header card">
        <p>
          The top card identifies the case at a glance: the <strong>reference</strong> (e.g.
          AE-042) with the clinic beneath it and the region under that, and the action buttons
          (<strong>Share</strong>, <strong>Mark resolved</strong>, <strong>Delete</strong>, as your
          role allows) on the right.
        </p>
        <p>
          The badge row underneath does two jobs. On the left, the <strong>Severity</strong> and{' '}
          <strong>Status</strong> badges are controls: if your role can change them they open as
          dropdowns, and every change asks you to confirm and is logged. On the right sit the
          descriptors — the event type or treatment and the case's tags.
        </p>
        <p>
          The <strong>clocks row</strong> closes the card: the response-SLA chip on coded treatment
          cases (whose turn it is and the deadline, with <strong>Manage</strong> for Supervisors)
          and the legal timeline bar. Both have their own articles.
        </p>
      </KbSection>

      <KbSection title="The tabs" delay={1}>
        <p>
          Under the header, tabs split the case into pages. <strong>Overview</strong> is where
          day-to-day work happens
          <VisibleTo user={user} when={(u) => u?.role !== 'ClinicManager'}>
            ; <strong>Report</strong> builds a printable PDF of the case
          </VisibleTo>
          <VisibleTo user={user} when={(u) => !!u?.canSeeCosts}>
            ; <strong>Costs</strong> holds the case's cost lines
          </VisibleTo>
          ; and <strong>History</strong> is the full record of everything that has happened on the case.
        </p>
      </KbSection>

      <KbSection title="Overview — the main column" delay={2}>
        <StepList steps={[
          <><strong>Incident details</strong> — the facts of the event, arranged for its type (treatment, near miss or clinic incident).</>,
          <><strong>Medical notes</strong> — the running clinical notes (titled just <strong>Notes</strong> on near-miss and clinic-incident cases).</>,
          <><strong>Required documents &amp; sign-offs</strong> — the checklist of documents the case still needs; each row is a slot that shows whether its file has arrived.</>,
          <><strong>Attachments</strong> — every other file on the case; click one to preview it in place.</>,
          <><strong>Comments</strong> — the discussion thread, at the bottom.</>,
        ]} />
        <p>A closed case adds a <strong>Case resolution</strong> card with the closing summary and date.</p>
      </KbSection>

      <KbSection title="Overview — the right column" delay={3}>
        <p>
          The narrow column keeps reference material in view while you scroll:{' '}
          <strong>Case information</strong> (reference, clinic, region, dates and current badges),{' '}
          <strong>Staff</strong> (the staff member, clinic manager and case contact), and a{' '}
          <strong>History</strong> card previewing the ten most recent events —{' '}
          <em>View all</em> jumps to the full History tab.
        </p>
        <Callout tone="info">
          This page is just the map — searching, badges, the legal timeline and the audit trail
          each have their own article in this section.
        </Callout>
      </KbSection>
    </>
  );
}

function BadgesExplainedBody({ user }) {
  return (
    <>
      <KbSection title="Four families, four looks">
        <p>
          Every pill in the tracker belongs to one of four families, and each family has a
          deliberately different look — so you can tell a row's severity, progress and
          event type apart at a glance, without reading a single label.
        </p>
      </KbSection>

      <KbSection title="Severity — solid and warm" delay={1}>
        <p>
          Severity codes are the only <strong>solid, warm-filled</strong> pills: the ramp runs
          yellow to deep maroon as the clinical picture escalates. Only coded treatments (LHR
          and AWI) carry a severity code.
        </p>
        <BadgeRow>
          <span className="badge sev-1"><span className="dot" />Code 1 · Mild</span>
          <span className="badge sev-2"><span className="dot" />Code 2 · Moderate</span>
          <span className="badge sev-3"><span className="dot" />Code 3 · Severe</span>
          <span className="badge sev-4"><span className="dot" />Code 4 · Serious</span>
          <span className="badge sev-5"><span className="dot" />Code 5 · Critical</span>
        </BadgeRow>
      </KbSection>

      <KbSection title="Status — soft, cool, with a dot" delay={2}>
        <p>
          Status badges are <strong>soft-tinted with a leading dot</strong>, on a cool ramp that
          runs blue to teal — a case literally cools toward brand teal as it nears resolution.{' '}
          <strong>New</strong> gets an extra ring so fresh cases stand out; an escalation-style
          status is the one deliberate exception and stays red. The names below are the app
          defaults — your admin may have configured different ones.
        </p>
        <BadgeRow>
          <span className="badge status-new"><span className="dot" />New</span>
          <span className="badge status-open"><span className="dot" />Open</span>
          <span className="badge status-inv"><span className="dot" />Pending</span>
          <span className="badge status-wait"><span className="dot" />Waiting on Client</span>
          <span className="badge status-res"><span className="dot" />Closed</span>
          <span className="badge status-esc"><span className="dot" />Escalated</span>
        </BadgeRow>
        <StatusFlowDemo />
      </KbSection>

      <KbSection title="Event type — neutral and dashed" delay={3}>
        <p>
          Non-treatment cases show a <strong>grey, dashed-border</strong> tag in the Event
          column where a treatment badge would be. No colour at all — it marks a <em>kind</em> of
          case, not a state or a severity, so it never competes with the other two families.
        </p>
        <BadgeRow>
          <span className="badge event-type">Near miss</span>
          <span className="badge event-type">Clinic incident</span>
        </BadgeRow>
      </KbSection>

      <VisibleTo user={user} when={(u) => !!u?.canSeeCosts}>
        <KbSection title="Cost categories — grey fill, coloured outline" delay={3}>
          <p>
            Cost-category pills label the line items on a case's Costs tab. The fill is{' '}
            <strong>always grey</strong>; only the border and text carry the category's colour,
            and there is <strong>no dot</strong> — that combination is what separates them from
            the tinted status badges when both appear on screen. Categories are admin-configured;
            these are the defaults.
          </p>
          <BadgeRow>
            <span className="cost-pill cost-refund">Refund</span>
            <span className="cost-pill cost-goodwill">Goodwill</span>
            <span className="cost-pill cost-corrective">Corrective treatment</span>
            <span className="cost-pill cost-medical">Medical / GP</span>
            <span className="cost-pill cost-legal">Legal</span>
            <span className="cost-pill cost-compensation">Compensation</span>
            <span className="cost-pill cost-other">Other</span>
          </BadgeRow>
        </KbSection>
      </VisibleTo>
    </>
  );
}

function LegalTimelineBody() {
  return (
    <>
      <KbSection title="What the bar measures">
        <p>
          Every case header carries a <strong>Timeline</strong> bar tracking the legal deadline.
          It starts at the <strong>Date of Incident</strong> (falling back to the date the case
          was reported when no incident date is recorded) and runs to the legal threshold —{' '}
          <strong>6 months by default</strong>, a value your admin can change. Hover the end dot
          for the threshold date and the days remaining, e.g. <em>Legal threshold · 12 Mar 2027 ·
          91d to go</em>.
        </p>
        <LegalBarDemo />
      </KbSection>

      <KbSection title="Reading the bar" delay={1}>
        <StepList steps={[
          <>The fill advances with time and shifts <strong>teal → amber → red</strong> — colour alone tells you how much runway is left.</>,
          <>A dot marks each month; passed dots light up, and hovering one shows its exact date.</>,
          <>Small dots along the track are the case's own history events, so you can see activity (or a quiet stretch) against the deadline.</>,
          <>The endpoint is the threshold itself — hover it for the exact date.</>,
        ]} />
      </KbSection>

      <KbSection title="As the threshold approaches" delay={2}>
        <p>
          Treat amber as a prompt: chase outstanding required documents, nudge the thread with a
          comment, and make sure the notes reflect where things stand. A red bar means weeks, not
          months — prioritise moving the case to resolution while everything is still fresh and
          documented.
        </p>
        <Callout tone="warn">
          The bar does not stop at the deadline. Past the threshold it stays dark red and the end
          dot's hover reads <em>days past</em> — an overdue case stays visibly overdue.
        </Callout>
      </KbSection>
    </>
  );
}

function CaseHistoryBody({ user }) {
  return (
    <>
      <KbSection title="Where to find it">
        <p>
          The <strong>History</strong> tab on a case is the complete record, newest first and
          grouped by day. For a quick glance, the <strong>History</strong> card in the Overview
          tab's right column previews the ten most recent events — <em>View all</em> takes you to
          the full tab.
        </p>
      </KbSection>

      <KbSection title="What gets recorded" delay={1}>
        <p>An entry is written automatically for every action on a case:</p>
        <StepList steps={[
          <>Case milestones — created, resolved, closed, reopened.</>,
          <>Status and severity changes, and edits to the case file.</>,
          <>Comments posted, edited or deleted.</>,
          <>Attachments uploaded or deleted.</>,
          // Cost events are filtered out of history for non-cost roles — the
          // article mirrors that gate rather than teasing the feature.
          ...(user?.canSeeCosts ? [
            <>Cost lines added, updated or deleted — these entries only appear for roles with
            access to the cost feature.</>,
          ] : []),
        ]} />
        <VisibleTo user={user} when={(u) => u?.role !== 'ClinicManager'}>
          <p>
            History respects comment visibility: entries for private comments (and their
            attachments) are hidden from Clinic Manager accounts, just like the comments
            themselves.
          </p>
        </VisibleTo>
      </KbSection>

      <KbSection title="Automatic, attributed, permanent" delay={2}>
        <p>
          Every entry records <strong>who</strong> did it, <strong>when</strong>, and for field
          changes the old and new value side by side. You never write a history entry yourself —
          the app creates them as things happen — and there is no way to edit or remove one.
          That is the point: when you confirm a change, the dialog reminds you it{' '}
          <em>"will be saved and logged in the case history"</em>, and that log is what an audit
          can rely on later.
        </p>
      </KbSection>
    </>
  );
}

function SharePrintBody({ user }) {
  return (
    <>
      <KbSection title="Sharing a link">
        <p>
          Every page in the tracker has a stable address you can paste into an email or Teams
          message. On <strong>All Cases</strong> and the Dashboard, the <strong>Copy link</strong>{' '}
          button in the page header copies the current view <em>including your active filters and
          search</em> — the recipient opens exactly the slice of the list you were looking at. For
          a single case, copy the address from the browser bar while you're on it.
        </p>
        <Callout tone="info">
          A link carries no access of its own. Whoever opens it signs in as themselves and sees
          the case only if their own role covers it — so links are always safe to share
          internally.
        </Callout>
      </KbSection>

      <VisibleTo user={user} when={(u) => u?.role !== 'ClinicManager'}>
        <KbSection title="Building a PDF report" delay={1}>
          <p>
            The <strong>Report</strong> tab on a case turns it into a formatted document. The left
            side is a live page preview; on the right, <strong>Report sections</strong> lets you
            tick exactly what to include:
          </p>
          <StepList steps={[
            <><strong>Case overview</strong> — reference, status, severity and key dates.</>,
            <><strong>Clinic &amp; location</strong> — clinic name, region and country.</>,
            <><strong>Incident details</strong> — the facts of the event, adapted to its type.</>,
            <><strong>Staff information</strong> — the people involved.</>,
            <><strong>Medical notes</strong> (or <strong>Notes</strong>) — the full written notes.</>,
            <><strong>Resolution</strong> — closed date and resolution text, on closed cases.</>,
            <><strong>Timeline</strong> — the legal-threshold bar and day count.</>,
            <><strong>Documents &amp; attachments</strong>, <strong>Comments</strong> and <strong>History</strong> — the checklist and files, the public thread, and the audit trail, so the report can carry the whole case.</>,
          ]} />
          <p>
            The preview updates as you tick and untick, so you always know what will come out.
            It is laid out in printed pages — when the report runs past one page a pager above
            the preview steps through them. An <strong>Attachments</strong> panel underneath lists
            the case files for download alongside the report.
          </p>
        </KbSection>

        <KbSection title="Printing it to PDF" delay={2}>
          <StepList steps={[
            <>Click <strong>Export as PDF</strong> (at least one section must be ticked).</>,
            <>A print window opens with the finished report and your browser's print dialog on top.</>,
            <>Choose <strong>Save as PDF</strong> as the destination and save — that file is what you attach or archive.</>,
          ]} />
          <Callout tone="warn">
            If nothing opens, your browser blocked the pop-up — allow pop-ups for this site and
            click Export again.
          </Callout>
        </KbSection>
      </VisibleTo>
    </>
  );
}

export const FINDING_CASES_ARTICLES = [
  {
    id: 'case-list',
    title: 'The All Cases list',
    category: 'finding-cases',
    summary: 'Filters, sorting, paging and the table itself — finding your way around the master list.',
    keywords: ['all cases', 'list', 'table', 'filter', 'sort', 'page', 'columns', 'status filter',
      'saved view', 'saved views', 'save view', 'favourite', 'favorite', 'favourite view', 'my view', 'default view', 'views'],
    visibleTo: () => true,
    Body: CaseListBody,
  },
  {
    id: 'search',
    title: 'Searching for a case',
    category: 'finding-cases',
    summary: 'What the search box matches, and how to pin down one case fast.',
    keywords: ['search', 'find', 'lookup', 'case id', 'reference', 'clinic', 'therapist', 'staff', 'query'],
    visibleTo: () => true,
    Body: SearchBody,
  },
  {
    id: 'case-detail-tour',
    title: 'Around the case page',
    category: 'finding-cases',
    summary: 'The case header, the tabs, and where everything lives on the Overview.',
    keywords: ['case page', 'detail', 'tabs', 'overview', 'layout', 'header', 'panels', 'tour'],
    visibleTo: () => true,
    Body: CaseDetailTourBody,
  },
  {
    id: 'badges-explained',
    title: 'What the badges mean',
    category: 'finding-cases',
    summary: 'Severity, status, event-type and cost pills — how to tell the four families apart.',
    keywords: ['badge', 'pill', 'severity', 'status', 'colour', 'code', 'event type', 'legend'],
    visibleTo: () => true,
    Body: BadgesExplainedBody,
  },
  {
    id: 'legal-timeline',
    title: 'The legal deadline',
    category: 'finding-cases',
    summary: 'The Timeline bar on every case: what it measures and how to read it.',
    keywords: ['legal', 'timeline', 'deadline', 'threshold', '6 months', 'days remaining', 'overdue'],
    visibleTo: () => true,
    Body: LegalTimelineBody,
  },
  {
    id: 'case-history',
    title: 'Case history & audit trail',
    category: 'finding-cases',
    summary: 'The automatic, unchangeable record of everything that happens on a case.',
    keywords: ['history', 'audit', 'trail', 'log', 'events', 'who changed', 'record'],
    visibleTo: () => true,
    Body: CaseHistoryBody,
  },
  {
    id: 'share-print',
    title: 'Sharing a case and exporting a PDF',
    category: 'finding-cases',
    summary: 'Copy a link colleagues can open with their own access, or build a printable report.',
    keywords: ['share', 'link', 'copy link', 'pdf', 'export', 'print', 'report', 'download'],
    visibleTo: () => true,
    Body: SharePrintBody,
  },
];
